import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import {DefaultRootState} from 'react-redux';

import {RESET_URL} from 'app/urls';

import {deleteAuthTokenFromLocalStorage} from './token-local-storage';

export type TokenAuthErrorType =
  | 'invalid-token'
  | 'required-not-in-scope'
  | 'missing-token'
  | 'inactive-user'
  | 'unknown';

interface DetectingAuthState {
  type: null;
  error?: null;
}

interface TokenAuthState {
  type: 'token';
  token: string;
  error?: null | TokenAuthErrorType;
}

interface SessionAuthState {
  type: 'session-cookie';
  error?: null | 'not-authenticated';
}

export type AuthState = DetectingAuthState | TokenAuthState | SessionAuthState;

const initialState: AuthState = {
  type: null,
  error: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: initialState as AuthState,
  reducers: {
    setAuthType: (
      state,
      action: PayloadAction<SessionAuthState | TokenAuthState>
    ) => action.payload,
    setTokenAuthError: (state, action: PayloadAction<TokenAuthErrorType>) => {
      if (state.type === 'token') {
        state.error = action.payload;
      }
    },
    setCookieAuthError: (
      state,
      action: PayloadAction<SessionAuthState['error']>
    ) => {
      if (state.type === 'session-cookie') {
        state.error = action.payload;
      }
    },
  },
});

// Action creators are generated for each case reducer function
export const {setAuthType, setTokenAuthError, setCookieAuthError} =
  authSlice.actions;

export const authReducer = authSlice.reducer;

export const authTypeSelector = createSelector<
  [(state: DefaultRootState) => AuthState],
  AuthState
>([(state) => state.auth], (auth) => auth);

export const logoutTokenUser = createAsyncThunk(
  'auth/logout',
  async (params: void, {getState}) => {
    const authType = authTypeSelector(getState() as DefaultRootState).type;

    if (authType === 'token') {
      deleteAuthTokenFromLocalStorage();
      window.location.replace(RESET_URL);
    }
  }
);
